import type { AxiosInstance } from 'axios'
import { getAuth } from '@/plugins/auth'
import { useAuthStore } from '@/stores/auth.store'
import router from '@/router'
import { useToast } from '@/composables/useToast'
import { isOnlineReviewsNotEnabledError } from '@/utils/error-handler'

/**
 * Attaches standard request interceptor to an Axios instance.
 * - Gets Bearer token from Cognito session or impersonation store
 * - Adds CORS header in development mode
 *
 * `ownSession` always sends the admin's own Cognito token, even while impersonating, for a service
 * that checks who the admin is (the Claude Fleet API) rather than acting as the merchant.
 */
export function attachRequestInterceptor(client: AxiosInstance, options?: { ownSession?: boolean }): void {
  client.interceptors.request.use(
    async config => {
      const authStore = useAuthStore()

      // Get token: impersonation takes priority, then Cognito session
      let tokenToUse: string | undefined

      if (!options?.ownSession && authStore.isImpersonating && authStore.accessToken) {
        tokenToUse = authStore.accessToken
      } else {
        try {
          const provider = getAuth()
          tokenToUse = await provider.getAccessToken()
        } catch {
          // No valid session
        }
      }

      if (tokenToUse) {
        config.headers.Authorization = `Bearer ${tokenToUse}`
      }

      // Add CORS header only for local development
      if (import.meta.env.DEV || window.location.hostname === 'localhost') {
        config.headers['x-ttl-allow-cors'] = window.location.origin
      }

      return config
    },
    error => {
      return Promise.reject(error)
    }
  )
}

/**
 * A page can have several requests in flight, so one expired impersonation token
 * produces several 401s. They share one restore attempt and one toast.
 */
let endImpersonationInFlight: Promise<void> | null = null

/**
 * Upper bound on how long we wait for the restore to settle. No axios client in
 * this repo sets a request `timeout`, so `endImpersonation()` can hang forever on
 * a dropped connection — which would leave `endImpersonationInFlight` stuck and
 * silently swallow every later 401. On timeout we fall back to login instead.
 */
const RESTORE_TIMEOUT_MS = 8000

/**
 * Ends impersonation and restores the admin's own Cognito session, falling back to a
 * full logout if that session is gone too. checkAuth() swallows its errors and returns
 * false, so an unusable session shows up as a resolve, not a rejection — both paths
 * have to be handled. A hung restore is bounded by RESTORE_TIMEOUT_MS.
 */
function endImpersonationOnce(authStore: ReturnType<typeof useAuthStore>): void {
  if (endImpersonationInFlight) return

  const { showWarning } = useToast()

  const fallBackToLogin = () => {
    showWarning('Your session expired. Please sign in again.', 'Session Expired')
    authStore.logout()
    router.push('/admin/login')
  }

  const restore = authStore.endImpersonation()
  // Swallow a late rejection so it isn't unhandled if the timeout wins the race.
  restore.catch(() => {})

  let timeoutId: ReturnType<typeof setTimeout>
  const bounded = Promise.race([
    restore,
    new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => reject(new Error('endImpersonation timed out')), RESTORE_TIMEOUT_MS)
    }),
  ])

  endImpersonationInFlight = bounded
    .then(() => {
      if (!authStore.isAuthenticated) {
        fallBackToLogin()
        return
      }
      showWarning(
        'Your impersonation session expired. Returned to your admin account.',
        'Impersonation Ended'
      )
    })
    .catch(fallBackToLogin)
    .finally(() => {
      clearTimeout(timeoutId)
      endImpersonationInFlight = null
    })
}

/**
 * Attaches standard response interceptor to an Axios instance.
 * - Logs 500+ errors via console.error
 * - Handles 401 by logging out and redirecting to login, or by ending impersonation
 * - Handles 403 by showing permission denied toast
 */
export function attachResponseInterceptor(
  client: AxiosInstance,
  options?: { skipAuthHandling?: boolean }
): void {
  client.interceptors.response.use(
    response => {
      return response
    },
    error => {
      // Keep important error logging for debugging API issues
      if (error.response?.status >= 500) {
        console.error(
          'API Server Error:',
          error.response?.status,
          error.config?.url,
          error.response?.data || error.message
        )
      }

      if (!options?.skipAuthHandling) {
        // Handle 401 - Unauthorized (session expired)
        if (error.response?.status === 401) {
          const authStore = useAuthStore()

          if (authStore.isImpersonating) {
            // The active token while impersonating is the impersonation token, not the
            // admin's Cognito session, so a 401 means only impersonation expired.
            endImpersonationOnce(authStore)
          } else {
            authStore.logout()
            router.push('/admin/login')
          }
        }

        // Handle 403 - Forbidden (no permission)
        // Exception: the Online-Reviews-not-enabled gate (TE-13294) also throws 403, but it's a
        // normal per-merchant state, not a permission problem -- a caller that recognizes it
        // (useMerchantAudit's responseTemplates section) shows its own informational message,
        // so the generic "Permission Denied" toast would be actively misleading here.
        if (error.response?.status === 403 && !isOnlineReviewsNotEnabledError(error)) {
          const { showError } = useToast()
          showError('You do not have permission to perform this action', 'Permission Denied')
        }
      }

      return Promise.reject(error)
    }
  )
}

/**
 * Convenience function to attach both interceptors at once.
 */
export function attachInterceptors(
  client: AxiosInstance,
  options?: { skipAuthHandling?: boolean; ownSession?: boolean }
): void {
  attachRequestInterceptor(client, { ownSession: options?.ownSession })
  attachResponseInterceptor(client, { skipAuthHandling: options?.skipAuthHandling })
}
